import { useCallback, useRef, useState } from 'react'
import { useNotifications } from '@/lib/useNotifications'
import type { AgentSessionProps, SearchResultSession } from '@/types'

export function useChatSessions(
  initialSessions: AgentSessionProps[],
  initialSessionId: string | null,
  initialSession: AgentSessionProps | null,
  projectId: string,
) {
  const [sessions, setSessions] = useState<AgentSessionProps[]>(initialSessions)
  const [activeSessionId, setActiveSessionId] = useState<string | null>(initialSessionId)
  const [activeSession, setActiveSession] = useState<AgentSessionProps | null>(initialSession)

  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<SearchResultSession[] | null>(null)
  const [searchLoading, setSearchLoading] = useState(false)
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const updateSessionInList = useCallback((session: AgentSessionProps) => {
    setActiveSession(session)
    setSessions((prev) => {
      const exists = prev.some((s) => s.id === session.id)
      if (exists) return prev.map((s) => s.id === session.id ? session : s)
      return [session, ...prev]
    })
  }, [])

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query)

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    if (!query.trim()) {
      setSearchResults(null)
      setSearchLoading(false)
      return
    }

    setSearchLoading(true)
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await fetch(
          `/projects/${projectId}/chats/search?q=${encodeURIComponent(query.trim())}`,
          { headers: { Accept: 'application/json' } }
        )
        if (response.ok) {
          const data = await response.json() as { sessions: SearchResultSession[] }
          setSearchResults(data.sessions)
        }
      } catch {
        // Ignore network errors
      } finally {
        setSearchLoading(false)
      }
    }, 300)
  }, [projectId])

  const displayedSessions = searchResults ?? sessions

  useNotifications(useCallback((data: Record<string, unknown>) => {
    if (data.type === 'session_title_updated' && typeof data.title === 'string') {
      const sessionId = data.session_id as string
      const title = data.title
      setActiveSession((prev) => prev && prev.id === sessionId ? { ...prev, title } : prev)
      setSessions((prev) => prev.map((s) => s.id === sessionId ? { ...s, title } : s))
    }
  }, []))

  return {
    sessions,
    setSessions,
    activeSessionId,
    setActiveSessionId,
    activeSession,
    setActiveSession,
    searchQuery,
    searchLoading,
    displayedSessions,
    handleSearch,
    updateSessionInList,
  }
}
